// src/webideal-jobs/pages/Services.jsx

import React from "react";


import {

  FileText,
  Sparkles,
  Target,
  MessageSquare,
  TrendingUp,
  Award,
  BookOpen,
  Video,

  CheckCircle2,
  ArrowRight
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function Services() {
  const services = [
    {
      icon: FileText,
      title: "Resume Builder",
      description: "Create ATS-friendly resumes with AI-powered suggestions",
      features: ["Multiple templates", "AI content suggestions", "PDF export"],
      price: "Free",
      badge: "Popular",
      color: "blue",
    },
    {
      icon: Sparkles,
      title: "Premium Job Access",
      description: "Access exclusive job opportunities before others",
      features: ["Early alerts", "Priority applications", "Hidden jobs"],
      price: "₹999/yr",
      badge: "Best Value",
      color: "purple",
    },
    {
      icon: Target,
      title: "Career Coach",
      description: "One-on-one guidance from industry experts",
      features: ["Resume review", "Interview prep", "Career planning"],
      price: "₹4999/session",
      badge: null,
      color: "green",
    },
    {
      icon: MessageSquare,
      title: "AI Interview Prep",
      description: "Practice interviews with AI and get instant feedback",
      features: ["Mock interviews", "Question bank", "Analytics"],
      price: "₹499/month",
      badge: "New",
      color: "orange",
    },
    {
      icon: TrendingUp,
      title: "Skill Assessment",
      description: "Test your skills and get certified",
      features: ["Industry tests", "Certificates", "Profile badge"],
      price: "Free",
      badge: null,
      color: "pink",
    },
    {
      icon: Award,
      title: "Priority Support",
      description: "24/7 dedicated support for your job search",
      features: ["Instant response", "Profile optimization", "Tracking"],
      price: "₹299/month",
      badge: null,
      color: "indigo",
    },
  ];

  const learningResources = [
    {
      icon: BookOpen,
      title: "Career Guides",
      description: "Expert-written guides on job growth",
      count: "500+ guides",
    },
    {
      icon: Video,
      title: "Video Tutorials",
      description: "Learn from industry professionals",
      count: "100+ videos",
    },
    {
      icon: MessageSquare,
      title: "Webinars",
      description: "Weekly live sessions with experts",
      count: "Weekly events",
    },
  ];

  const colorClasses = {
    blue: { bg: "bg-blue-50", icon: "text-blue-600" },
    purple: { bg: "bg-purple-50", icon: "text-purple-600" },
    green: { bg: "bg-green-50", icon: "text-green-600" },
    orange: { bg: "bg-orange-50", icon: "text-orange-600" },
    pink: { bg: "bg-pink-50", icon: "text-pink-600" },
    indigo: { bg: "bg-indigo-50", icon: "text-indigo-600" },
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* HERO SECTION */}
      <section className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white py-16">
        <div className="w-full px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Accelerate Your Career Journey
          </h1>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Professional services and tools to help you land your dream job faster.
          </p>

          <div className="flex items-center justify-center gap-3">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 rounded-lg font-semibold">
              Explore Services
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-lg font-semibold">
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="w-full px-4 py-12">

        {/* SERVICES */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Our Services</h2>
          <p className="text-gray-600">Choose the right service to boost your career</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {services.map((service, idx) => {
            const Icon = service.icon;
            const colors = colorClasses[service.color];

            return (
              <Card
                key={idx}
                className="p-6 hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center`}>
                    <Icon className={`w-7 h-7 ${colors.icon}`} />
                  </div>

                  {service.badge && (
                    <Badge className="bg-gradient-to-r from-orange-500 to-pink-500 text-white border-0">
                      {service.badge}
                    </Badge>
                  )}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4">{service.description}</p>

                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-2xl font-bold text-gray-900">{service.price}</span>

                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 rounded-lg">
                    Get Started <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* LEARNING RESOURCES */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Free Learning Resources
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {learningResources.map((res, idx) => {
              const Icon = res.icon;
              return (
                <Card
                  key={idx}
                  className="p-6 hover:shadow-lg transition-all border border-gray-100 group"
                >
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-100">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>

                  <h3 className="font-bold text-gray-900 mb-2">{res.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">{res.description}</p>

                  <Badge variant="outline" className="text-blue-600 border-blue-200">
                    {res.count}
                  </Badge>
                </Card>
              );
            })}
          </div>
        </section>

        {/* CTA BANNER */}
        <Card className="p-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">Ready to take your career to the next level?</h2>
              <p className="text-blue-100">
                Join thousands of professionals who upgraded their career with our services.
              </p>
            </div>

            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 rounded-lg font-semibold">
              Get Started Today <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </Card>
      </main>
    </div>
  );
}
